import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './RedelegateModal.css'; // Reuse the same CSS for now
import { DPOS_CONTRACT_ADDRESS, TARGET_VALIDATOR } from '../../config/networkConfig';
import { DposInterface } from '../../typechain-types/dpos_contract_interface.sol/DposInterface';
import dposInterface from '../../artifacts/contracts/dpos_contract_interface.sol/DposInterface.json';
import { ValidatorDelegation } from '../../services/DelegationService';
const dposInterfaceAbi = dposInterface.abi;

interface RedelegateModalProps {
  isOpen: boolean;
  onClose: () => void;
  otherValidators: ValidatorDelegation[];
  targetValidatorName?: string; // Optional target validator name
}

const RedelegateModal: React.FC<RedelegateModalProps> = ({ 
  isOpen, 
  onClose, 
  otherValidators,
  targetValidatorName = "Icelands 1st validator" 
}) => {
  // Initialize state
  const [selectedValidator, setSelectedValidator] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>("0");
  const [isManualInput, setIsManualInput] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Ref for the modal content
  const modalContentRef = useRef<HTMLDivElement>(null);
  
  // Set the minimum amount
  const minAmount = 1000;

  // Update max amount when a validator is selected
  useEffect(() => {
    if (selectedValidator) {
      const validator = otherValidators.find(v => v.address === selectedValidator);
      if (validator) {
        // Convert BigInt to number for UI (potentially lossy for very large numbers)
        const amountInEther = parseFloat(ethers.formatEther(validator.amount));
        setMaxAmount(amountInEther);
        
        // Set initial amount based on different scenarios
        let initialAmount;
        
        if (amountInEther <= minAmount) {
          // If max available is less than minimum, use max
          initialAmount = amountInEther;
        } else if (amountInEther <= 100) {
          // If max is between minAmount and 100, use minAmount
          initialAmount = minAmount;
        } else {
          // Otherwise, round down to nearest 100
          initialAmount = Math.floor(Math.min(amountInEther, 100) / 100) * 100;
          if (initialAmount < minAmount) initialAmount = 100;
        }
        
        setAmount(initialAmount);
        setInputValue(initialAmount.toString());
      }
    } else {
      setMaxAmount(0);
      setAmount(0);
      setInputValue("0");
    }
  }, [selectedValidator, otherValidators]);
  
  // Handle validator selection
  const handleValidatorSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValidator(e.target.value);
    setError(null);
  };
  
  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    
    // Special case for min and max values
    if (newValue === minAmount || newValue === maxAmount) {
      setAmount(newValue);
      setInputValue(newValue.toString());
    } else {
      // Round to nearest 100
      const roundedValue = Math.round(newValue / 100) * 100;
      setAmount(roundedValue);
      setInputValue(roundedValue.toString());
    }
    
    setIsManualInput(false);
    setError(null);
  };
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsManualInput(true);
    setError(null);
    
    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      // Enforce min and max
      if (parsedValue >= minAmount && parsedValue <= maxAmount) {
        setAmount(parsedValue);
      } else if (parsedValue < minAmount) {
        setAmount(minAmount);
      } else if (parsedValue > maxAmount) {
        setAmount(maxAmount);
      }
    }
  };
  
  // When manual input loses focus, ensure the value is valid
  const handleInputBlur = () => {
    if (isManualInput) {
      const parsedValue = parseFloat(inputValue);
      if (isNaN(parsedValue) || parsedValue < minAmount) {
        setAmount(minAmount);
        setInputValue(minAmount.toString());
      } else if (parsedValue > maxAmount) {
        setAmount(maxAmount);
        setInputValue(maxAmount.toString());
      } else {
        setAmount(parsedValue);
        setInputValue(parsedValue.toString());
      }
      setIsManualInput(false);
    }
  };
  
    // Handle redelegate button click
    const handleRedelegateNow = async () => {
        if (isSubmitting) return;
        
        try {
        setIsSubmitting(true);
        setError(null);
        
        // Check if validator is selected
        if (!selectedValidator) {
            throw new Error('Please select a validator to redelegate from');
        }
        
        // Check if amount is valid
        if (amount < minAmount) {
            throw new Error(`Minimum redelegation amount is ${minAmount} TARA`);
        }
        
        if (amount > maxAmount) {
            throw new Error(`Insufficient staked balance. Maximum available is ${maxAmount} TARA`);
        }
        
        // Initialize provider and signer
        if (!window.ethereum) {
            throw new Error('MetaMask or another web3 provider is required');
        }
        
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        
        // Initialize DPOS contract
        const dposContract = new ethers.Contract(
            DPOS_CONTRACT_ADDRESS,
            dposInterfaceAbi,
            signer
        ) as unknown as DposInterface;
        
        // Convert amount to wei (1 TARA = 10^18 wei)
        const amountInWei = ethers.parseEther(amount.toString());
        
        console.log('Redelegating TARA:', {
            selectedValidator,
            TARGET_VALIDATOR,
            amount: amountInWei.toString()
        });
        // Call the redelegation method
        const tx = await dposContract.reDelegate(
            selectedValidator, // From validator
            TARGET_VALIDATOR, // To validator
            amountInWei, // Amount,
            { gasLimit: 500000 } 
        );
        
        // Wait for transaction to be mined
        console.log('Redelegation transaction submitted:', tx.hash);
        
        // Show success message and close modal
        alert(`Successfully redelegated ${amount} TARA to ${targetValidatorName}! Transaction hash: ${tx.hash} - This might take a few minutes!`);
        onClose();
        
        } catch (err) {
        console.error('Error redelegating TARA:', err);
        
        // Extract better error message if possible
        let errorMsg = 'An error occurred while redelegating';
        if (err instanceof Error) {
            if (err.message.includes('user rejected transaction')) {
            errorMsg = 'Transaction was rejected in your wallet';
            } else if (err.message.includes('insufficient funds')) {
            errorMsg = 'Insufficient funds for gas fees';
            } else {
            errorMsg = err.message;
            }
        }
        
        setError(errorMsg);
        } finally {
        setIsSubmitting(false);
        }
    };
  
  // Handle clicks outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  
  // Format amount with commas
  const formatAmount = (value: number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Format address to truncated form
  const formatAddress = (address: string): string => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="delegate-modal-overlay">
      <div className="delegate-modal-container">
        <div className="delegate-modal frost-effect" ref={modalContentRef}>
          <div className="delegate-modal-aurora"></div>
          
          <button className="delegate-modal-close" onClick={onClose}>
            &times;
          </button>
          
          <h2>Redelegate TARA</h2>
          <p className="delegate-modal-subtitle">
            Move your staked TARA to {targetValidatorName}
          </p>
          
          <div className="delegate-validator-select">
            <label htmlFor="validator-select">Select validator to redelegate from:</label>
            <select
              id="validator-select"
              value={selectedValidator}
              onChange={handleValidatorSelect}
              className="validator-dropdown"
              disabled={isSubmitting || otherValidators.length === 0}
            >
              <option value="">Select a validator</option>
              {otherValidators.map((validator) => (
                <option key={validator.address} value={validator.address}>
                  {formatAddress(validator.address)} - {formatAmount(parseFloat(ethers.formatEther(validator.amount)))} TARA
                </option>
              ))}
            </select>
          </div>
          
          <div className="delegate-amount-container">
            <div className="delegate-amount-display">
              <span className="amount-value">{formatAmount(amount)}</span>
              <span className="amount-currency">TARA</span>
            </div>
            
            <div className="delegate-slider-container">
            <input 
                type="range" 
                min={minAmount > maxAmount ? maxAmount : minAmount} 
                max={maxAmount || minAmount} 
                value={amount} 
                onChange={handleSliderChange}
                className="delegate-slider"
                step={100} // Keep the step at 100 TARA
                disabled={isSubmitting || !selectedValidator || maxAmount < minAmount}
            />
            <div className="delegate-slider-labels">
                <span>{formatAmount(minAmount > maxAmount ? maxAmount : minAmount)} TARA</span>
                <span>{formatAmount(maxAmount)} TARA</span>
            </div>
            </div>
            
            <div className="delegate-input-container">
              <label htmlFor="manual-amount">Or enter amount manually:</label>
              <div className="delegate-input-wrapper">
                <input 
                  id="manual-amount"
                  type="text" 
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className="delegate-input"
                  disabled={isSubmitting || !selectedValidator}
                />
                <span className="delegate-input-suffix">TARA</span>
              </div>
            </div>
          </div>
          
          <div className="delegate-info">
            <div className="info-item">
              <span className="info-label">Available to Redelegate:</span>
              <span className="info-value">{formatAmount(maxAmount)} TARA</span>
            </div>
            <div className="info-item">
              <span className="info-label">Min Redelegation:</span>
              <span className="info-value">{formatAmount(minAmount)} TARA</span>
            </div>
          </div>
          
          {error && (
            <div className="delegate-error">
              {error}
            </div>
          )}
          
          <div className="delegate-actions">
            <button 
              className="delegate-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              className="delegate-submit"
              onClick={handleRedelegateNow}
              disabled={
                isSubmitting || 
                !selectedValidator || 
                amount < minAmount || 
                amount > maxAmount || 
                maxAmount < minAmount
              }
            >
              {isSubmitting ? (
                <span className="button-spinner"></span>
              ) : 'Redelegate Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedelegateModal;